// Core engine types: the probe contract and its structured results.

/** Plan tier a probe belongs to / is gated by. Mirrors the server catalog (lowercase). */
export type ProbeTier = 'free' | 'business' | 'premium';

const TIER_ORDER: Record<ProbeTier, number> = { free: 0, business: 1, premium: 2 };

/** True when <paramref name="tier"/> is at or below the given ceiling (free ≤ business ≤ premium). */
export function tierAtMost(tier: ProbeTier, ceiling: ProbeTier): boolean {
  return TIER_ORDER[tier] <= TIER_ORDER[ceiling];
}

/** A measured signal value. `null` accompanies a blocked/unavailable measurement. */
export type ProbeValue = boolean | number | string | null;

/** Scored verdict for a single probe. Mirrors the server RunOutcome. */
export type ProbeOutcome = 'pass' | 'warn' | 'fail';

/**
 * Tenant-configured bounds a probe scores against. Mirrors the server's ProbeThresholds so the
 * client advisory score (display only) matches the authoritative shared score. The server
 * delivers the effective thresholds per probe in the engine config.
 */
export interface Thresholds {
  readonly min?: number;
  readonly max?: number;
  readonly expected?: ProbeValue;
  /** Categorical exact-membership allow-list. */
  readonly allowed?: readonly string[];
  /** Categorical substring match (any) — values are often joined lists, e.g. "H.264, VP9". */
  readonly containsAny?: readonly string[];
  /** Blocked is a good outcome (privacy probes where the signal simply isn't exposed). */
  readonly blockedIsPass?: boolean;
  /** Severity when the value doesn't meet the expectation: 'warn' (advisory) or 'fail' (default). */
  readonly onMismatch?: 'warn' | 'fail';
}

/**
 * Structured result of measuring one probe. "Blocked/unavailable" is a first-class outcome
 * (PRD §6.1): `run()` returns a measurement with `blocked: true` rather than throwing.
 */
export interface ProbeMeasurement {
  readonly probeId: string;
  readonly value: ProbeValue;
  readonly blocked: boolean;
  readonly note?: string;
}

/** Minimal fetch shape used by network probes and the results poster (injectable for tests). */
export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

/**
 * Snapshot of the browser/device/network surface the permissionless probes read. Injected
 * via <see cref="ProbeContext"/> so probes are pure and deterministically testable; the real
 * snapshot is built from globals by <c>readBrowserEnvironment()</c>. Optional fields are
 * absent when the platform doesn't expose the API — a probe treats that as blocked (PRD §6.1).
 */
export interface RuntimeEnvironment {
  readonly userAgent: string;
  readonly secureContext: boolean;
  readonly cookieEnabled: boolean;
  readonly globalPrivacyControl?: boolean;
  readonly languages: readonly string[];
  readonly timeZone?: string;
  readonly screen?: { readonly width: number; readonly height: number; readonly pixelRatio: number };
  readonly viewport?: { readonly width: number; readonly height: number };
  readonly hardware?: { readonly cores?: number; readonly memoryGb?: number };
  readonly graphics?: { readonly webgl2: boolean; readonly renderer?: string; readonly vendor?: string };
  readonly connection?: {
    readonly effectiveType?: string;
    readonly downlinkMbps?: number;
    readonly rttMs?: number;
  };
  readonly userAgentData?: {
    readonly platform: string;
    readonly mobile: boolean;
    readonly brands: readonly { readonly brand: string; readonly version: string }[];
  };
  /** Whether a 2D canvas context is obtainable (HTML5 Canvas capability). */
  readonly canvas2d?: boolean;
  /** navigator.webdriver — true under automation (bot.headless). */
  readonly webdriver?: boolean;
  /** HTML5 audio/video element support (media.audio-video). */
  readonly audioVideo?: boolean;
  /** WebAuthn / PublicKeyCredential availability (security.webauthn). */
  readonly webAuthn?: boolean;
  /** Multiple displays present, via the Window Management API's screen.isExtended (device.multi-display). */
  readonly multiDisplay?: boolean;
  /** Fullscreen API enabled (device.fullscreen). */
  readonly fullscreen?: boolean;
  /** navigator.maxTouchPoints (device.touch-pointer). */
  readonly maxTouchPoints?: number;
  /** Whether a fine pointer (mouse/trackpad) is present (device.touch-pointer). */
  readonly finePointer?: boolean;
  /** Whether a modern CSS baseline (grid, custom props, :has) is supported (capabilities.css). */
  readonly cssModern?: boolean;
  /** Which baseline CSS features are missing — for the capabilities.css failure note. */
  readonly cssMissing?: readonly string[];
  /** localStorage + sessionStorage usable (storage.local). */
  readonly localStorage?: boolean;
  /** WebAssembly available (capabilities.webassembly). */
  readonly webAssembly?: boolean;
  /** WebGPU available — navigator.gpu (capabilities.webgpu). */
  readonly webGpu?: boolean;
  /** Web Workers + Service Workers available (capabilities.workers). */
  readonly workers?: boolean;
  /** Which worker type(s) are missing — for the capabilities.workers failure note. */
  readonly workersMissing?: readonly string[];
  /** navigator.doNotTrack signals "1"/"yes" (privacy.dnt). */
  readonly doNotTrack?: boolean;
  /** OffscreenCanvas constructor available — off-main-thread rendering (capabilities.offscreen-canvas). */
  readonly offscreenCanvas?: boolean;
  /** SharedArrayBuffer available (needs cross-origin isolation) (capabilities.shared-array-buffer). */
  readonly sharedArrayBuffer?: boolean;
  /** IndexedDB present on this origin (storage.indexeddb). */
  readonly indexedDb?: boolean;
  /** High-dynamic-range display, via the `(dynamic-range: high)` media query (media.hdr). */
  readonly hdr?: boolean;
  /** Widest supported color gamut ('rec2020' | 'p3' | 'srgb') — note for the HDR probe (media.hdr). */
  readonly colorGamut?: string;
  /** Picture-in-Picture enabled — document.pictureInPictureEnabled (media.picture-in-picture). */
  readonly pictureInPicture?: boolean;
}

/** A network-measurement node the engine can target (from the server's engine config). */
export interface ApiNodeInfo {
  readonly region: string;
  readonly endpoint: string;
}

/**
 * Closest-node selection result (network.closest-node, PRD §11.3) — injected so the probe is
 * testable. The runner latency-races the configured nodes and points the net context at the
 * winner; with no nodes configured the runner falls back to the same-origin /diag.
 */
export interface NodeProbeContext {
  /** The node with the lowest /ping RTT, or null when none were reachable / none configured. */
  readonly selected: { readonly region: string; readonly endpoint: string; readonly latencyMs: number } | null;
  /** Every candidate considered, with its measured RTT (null = unreachable). */
  readonly candidates: readonly { readonly region: string; readonly endpoint: string; readonly latencyMs: number | null }[];
}

/** Dependencies the network probes need — injected so latency/throughput are testable. */
export interface NetProbeContext {
  readonly fetch: FetchLike;
  /** Monotonic clock in milliseconds (e.g. performance.now). */
  readonly now: () => number;
  readonly pingUrl: string;
  readonly downloadUrl: string;
  readonly downloadBytes: number;
  /** Endpoint that drains a POST body and reports timing — for the upload-throughput probe. */
  readonly uploadUrl?: string;
  /** Number of bytes the upload probe POSTs. */
  readonly uploadBytes?: number;
  /** Endpoint returning `{ epochMs }` server time — for the clock-skew probe. */
  readonly serverTimeUrl?: string;
  /** Wall-clock now in epoch milliseconds (e.g. Date.now) — for the clock-skew probe. */
  readonly wallClockNow?: () => number;
  /**
   * Streams ~bytes of raw (non-JSON) download, timing first byte → last byte — the accurate
   * throughput path: request setup (RTT) and client-side parsing never pollute the timed
   * window. Resolves null when streaming isn't available (probe falls back to the legacy
   * whole-request measurement).
   */
  readonly downloadTimed?: (
    bytes: number,
    signal?: AbortSignal,
  ) => Promise<{ readonly receivedBytes: number; readonly elapsedMs: number } | null>;
}

/**
 * Permissioned capabilities a probe may request. Each call triggers an explicit browser
 * permission prompt, so these probes only run when the user opts in (PRD §6.2). Injected
 * for testability; the browser implementation wraps getUserMedia / geolocation.
 */
export interface ProbeCapabilities {
  requestCameraMic?: () => Promise<{ readonly video: boolean; readonly audio: boolean }>;
  requestPreciseLocation?: () => Promise<{ readonly granted: boolean; readonly accuracyMeters?: number }>;
}

/** WebRTC reachability port (network.webrtc) — injected so ICE gathering is testable. */
export interface RtcProbeContext {
  /** Gathers ICE candidates against the configured STUN servers. */
  gather(): Promise<{ readonly reachedStun: boolean; readonly candidateTypes: readonly string[] }>;
}

/** Media decode + DRM capability port (media.codecs, media.drm) — injectable for testability. */
export interface MediaProbeContext {
  /** True if the platform reports it can decode the given video content type. */
  canDecode(contentType: string): Promise<boolean>;
  /** True if the given EME key system (e.g. com.widevine.alpha) is supported (media.drm). */
  drmSupport?(keySystem: string): Promise<boolean>;
}

/** Storage posture + quota port (storage.third-party-cookies, storage.indexeddb). */
export interface StorageProbeContext {
  /** true = blocked, false = allowed, null = indeterminate (a client-only check can't always tell). */
  thirdPartyCookiesBlocked(): Promise<boolean | null>;
  /** StorageManager.estimate() — total quota and current usage in bytes, or null if unavailable. */
  estimate?(): Promise<{ readonly quotaBytes: number; readonly usageBytes: number } | null>;
}

/**
 * Async device-hardware reads that aren't part of the synchronous environment snapshot
 * (device.cpu-architecture). Permissionless: CPU architecture comes from UA-CH high-entropy
 * hints (no prompt).
 */
export interface DeviceProbeContext {
  /** CPU architecture + bitness from UA-CH high-entropy hints (e.g. "x86 (64-bit)"), or null. */
  cpuArchitecture(): Promise<string | null>;
}

/** Permissions API snapshot port (privacy.permissions). */
export interface PermissionsProbeContext {
  /** The permission state ('granted'|'denied'|'prompt') or null if not queryable. Never prompts. */
  query(name: string): Promise<string | null>;
}

/**
 * Fingerprint-surface analysis port (privacy.fingerprint-hashes). Self-aware (PRD §16): the
 * implementation computes canvas/WebGL/audio fingerprints to judge each surface's behaviour
 * but returns only a verdict — never the raw fingerprint — so nothing identifying leaves the
 * browser or is persisted.
 */
export interface FingerprintProbeContext {
  analyze(): Promise<readonly { readonly surface: string; readonly state: 'stable' | 'randomized' | 'blocked' }[]>;
}

/**
 * Ad/tracker-blocker detection port (privacy.tracker-blocker, privacy.tracking-pixel). Tries
 * to load a first-party "bait" resource whose URL matches common filter-list patterns; a
 * blocker prevents the load. Returns true when the resource was blocked.
 */
export interface BlockerProbeContext {
  blocked(url: string, kind: 'script' | 'image'): Promise<boolean>;
}

/**
 * Endpoint-reachability matrix port (network.reachability). Probes a fixed set of target
 * classes the platform can actually test from a browser — HTTPS (our node + the public
 * internet), WebSocket, and WebRTC/STUN. Limited to 443 / WebSocket / WebRTC; arbitrary ports
 * can't be tested from a browser on App Service PaaS (documented limitation, PRD §7).
 */
export interface ReachabilityProbeContext {
  check(): Promise<readonly { readonly target: string; readonly reachable: boolean }[]>;
}

/**
 * Fingerprint-entropy port (privacy.fingerprint-entropy, PRD §14 self-aware). Computes coarse,
 * salted/truncated per-attribute buckets, sends them to the server's population model, and
 * returns the rarity. Self-aware: the client never sends a raw fingerprint, and the server
 * stores only independent per-attribute counts — never the joint tuple.
 */
export interface EntropyProbeContext {
  measure(): Promise<{ readonly bits: number; readonly oneInN: number } | null>;
}

/**
 * ISP port (network.isp). Asks the server who the caller's ISP is — the server resolves the
 * caller's own address against a local ASN database (the IP never leaves our infrastructure)
 * and returns only the coarse network identity. Null when the lookup is unavailable.
 */
export interface IspProbeContext {
  lookup(): Promise<{ readonly organization: string | null; readonly asn: number | null } | null>;
}

/** The advanced connection details behind network.ip-details. All fields best-effort. */
export interface IpDetails {
  readonly ip: string | null;
  readonly version: string | null;
  readonly port: number | null;
  readonly reverseDns: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly country: string | null;
}

/**
 * Public-IP port (network.public-ip / network.ip-details). Asks the server what address the
 * caller connected from — the cheap address() call serves the Free probe on every anonymous
 * scan; details() adds reverse DNS, source port and coarse location (Business). The full
 * address is shown to the visitor live; the server masks it at ingestion before anything is
 * stored (PRD §16).
 */
export interface IpProbeContext {
  address(): Promise<{ readonly ip: string | null; readonly version: string | null } | null>;
  details(): Promise<IpDetails | null>;
}

/** Ambient context passed to a probe's run(). */
export interface ProbeContext {
  readonly signal?: AbortSignal;
  readonly environment?: RuntimeEnvironment;
  readonly net?: NetProbeContext;
  readonly capabilities?: ProbeCapabilities;
  readonly rtc?: RtcProbeContext;
  readonly media?: MediaProbeContext;
  readonly storage?: StorageProbeContext;
  readonly permissions?: PermissionsProbeContext;
  readonly fingerprint?: FingerprintProbeContext;
  readonly blocker?: BlockerProbeContext;
  readonly device?: DeviceProbeContext;
  readonly node?: NodeProbeContext;
  readonly reachability?: ReachabilityProbeContext;
  readonly entropy?: EntropyProbeContext;
  readonly isp?: IspProbeContext;
  readonly ip?: IpProbeContext;
  /**
   * Per-probe measurement parameters keyed by probe id, delivered by the server run config
   * (effective = profile override ?? catalog default, server-clamped). Probes with tunable
   * measurements (the sustained-throughput pair) read their entry; absent = baked defaults.
   */
  readonly params?: Readonly<Record<string, Record<string, unknown> | undefined>>;
}

/**
 * A self-contained capability test. Each probe is registered into the catalog and
 * enabled/disabled purely by configuration — adding or removing a probe requires no
 * engine changes (PRD §6.1).
 */
export interface Probe {
  readonly id: string;
  readonly category: string;
  readonly tier: ProbeTier;
  readonly permissionRequired: boolean;

  /**
   * Saturates the network while it runs (the throughput probes). Runners execute heavy
   * probes ONE AT A TIME after the light set settles, so throughput isn't measured while
   * fighting itself and latency/jitter/loss aren't measured under artificial load.
   */
  readonly heavy?: boolean;

  /** Measures the signal. Must never throw for a blocked/unavailable result — return one. */
  run(context: ProbeContext): Promise<ProbeMeasurement> | ProbeMeasurement;

  /** Scores a measurement against optional thresholds. Authoritative scoring is server-side. */
  score(measurement: ProbeMeasurement, thresholds?: Thresholds): ProbeOutcome;
}

/** Builds an available (measured) result. */
export function measured(probeId: string, value: ProbeValue, note?: string): ProbeMeasurement {
  return { probeId, value, blocked: false, note };
}

/** Builds a blocked/unavailable result — a good, first-class outcome, not an error. */
export function blocked(probeId: string, note?: string): ProbeMeasurement {
  return { probeId, value: null, blocked: true, note };
}

/**
 * "via {region}" note fragment for node-targeted network probes when an API node was
 * selected (undefined on the same-origin /diag fallback — honest). Persisted with the
 * measurement, so shared results show where the numbers came from.
 */
export function viaNode(ctx: ProbeContext): string | undefined {
  return ctx.node?.selected ? `via ${ctx.node.selected.region}` : undefined;
}

/** Joins note fragments with ", ", dropping empties — e.g. "31.6 MB sampled, via westus3". */
export function joinNotes(...parts: (string | undefined)[]): string | undefined {
  const joined = parts.filter(Boolean).join(', ');
  return joined || undefined;
}
